import { getStateLMapping } from "../data/stateLMapping";

/**
 * Gets the terminology used for standards in a given state
 * by extracting the label before the first number in the first standard's title.
 * e.g., "GLE 1: ..." → "GLE", "Content Area 1: ..." → "Content Area",
 *        "K&S (c)(1): ..." → "K&S (c)", "Topical Category 1: ..." → "Topical Category"
 */
export const getStandardTerminology = (stateName: string): string => {
  const stateMapping = getStateLMapping(stateName);
  if (!stateMapping || stateMapping.length === 0) return "Standard";

  const firstStandard = stateMapping[0];
  if (!firstStandard?.title) return "Standard";

  // Extract everything before the first digit sequence (handles all formats)
  // e.g. "GLE 1: ..." → "GLE", "K&S (c)(1): ..." → "K&S (c)"
  const match = firstStandard.title.match(/^(.+?)[\s(]+(\d+)/);
  if (match) {
    return match[1].trim();
  }
  return "Standard";
};

/**
 * Gets the list of standards for a given state
 * Returns array of standard identifiers like ["Standard 1", "Standard 2"]
 * or ["Content Area 1", "Content Area 2"] or ["GLE 1", "GLE 2"]
 */
export const getStandardsListForState = (stateName: string): string[] => {
  const stateMapping = getStateLMapping(stateName);
  if (!stateMapping) {
    // Fallback: return generic standards if state not found
    return Array.from({ length: 15 }, (_, i) => `Standard ${i + 1}`);
  }

  const terminology = getStandardTerminology(stateName);
  // Use 1-based index to generate standard identifiers
  return stateMapping.map(
    (_: any, index: number) => `${terminology} ${index + 1}`,
  );
};

/**
 * Gets lesson IDs for a given standard in a specific state
 * Maps "Standard 1" or "GLE 1" to lesson IDs (e.g., "jobs-vs-careers")
 * Uses index-based lookup for robustness across all state title formats
 */
export const getLessonIdsForStandard = (
  standard: string,
  stateName: string,
): string[] => {
  const stateMapping = getStateLMapping(stateName);
  if (!stateMapping) return [];

  // Extract number from standard identifier and use as 1-based index
  const standardNum = Number.parseInt(standard.replace(/\D/g, ""), 10);
  if (
    Number.isNaN(standardNum) ||
    standardNum < 1 ||
    standardNum > stateMapping.length
  )
    return [];

  const standardMapping = stateMapping[standardNum - 1];
  if (!standardMapping || !standardMapping.chapters) return [];

  // Return all chapter IDs for this standard
  return standardMapping.chapters.map((ch: any) => ch.id);
};

/**
 * Gets the full standard title for a given standard identifier
 * e.g., "Standard 1" -> "Standard 1: Income & Careers"
 */
export const getStandardTitle = (
  standard: string,
  stateName: string,
): string => {
  const stateMapping = getStateLMapping(stateName);
  if (!stateMapping) return standard;

  // Extract number from standard identifier and use as 1-based index
  const standardNum = Number.parseInt(standard.replace(/\D/g, ""), 10);
  if (
    Number.isNaN(standardNum) ||
    standardNum < 1 ||
    standardNum > stateMapping.length
  )
    return standard;

  return stateMapping[standardNum - 1]?.title || standard;
};
